import React, { useMemo } from 'react';
import { Icon } from '@iconify/react';
import ReactApexChart from 'react-apexcharts';

/**
 * Consolidated complaints + tasks dashboard section.
 *
 * Consumed by:
 *   - src/pages/SocietyPortal/Dashboard/Dashboard.jsx      (single society)
 *   - src/pages/SocietyController/Dashboard/Dashboard.jsx  (cross-society + filter)
 *
 * The `data` prop is the operations dashboard payload from
 * GET /api/dashboard/operations — a single component renders both audiences
 * because the payload shape is identical.
 *
 * Props:
 *   data     — { complaints: {...}, tasks: {...}, trend_days } or null
 *   loading  — boolean
 *   filterSlot — optional React node rendered in the header (SocietyController
 *                supplies the society filter dropdown; SocietyPortal omits it)
 */

const STATUS_STYLE = {
    // complaints
    PENDING:     'tw-bg-amber-100 tw-text-amber-700',
    IN_PROGRESS: 'tw-bg-blue-100 tw-text-blue-700',
    COMPLETED:   'tw-bg-emerald-100 tw-text-emerald-700',
    REOPENED:    'tw-bg-rose-100 tw-text-rose-700',
    // tasks (extra)
    ASSIGNED:         'tw-bg-indigo-100 tw-text-indigo-700',
    ACCEPTED:         'tw-bg-cyan-100 tw-text-cyan-700',
    WAITING_APPROVAL: 'tw-bg-purple-100 tw-text-purple-700',
    ON_HOLD:          'tw-bg-gray-100 tw-text-gray-700',
    CLOSED:           'tw-bg-emerald-50 tw-text-emerald-600',
    CANCELLED:        'tw-bg-gray-100 tw-text-gray-500',
    REJECTED:         'tw-bg-rose-50 tw-text-rose-600'
};

const humanize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const KpiCard = ({ label, value, tone = 'slate', icon }) => {
    const toneMap = {
        slate:   'tw-bg-slate-50 tw-text-slate-700',
        rose:    'tw-bg-rose-50 tw-text-rose-700',
        amber:   'tw-bg-amber-50 tw-text-amber-700',
        emerald: 'tw-bg-emerald-50 tw-text-emerald-700',
        blue:    'tw-bg-blue-50 tw-text-blue-700'
    };
    return (
        <div className={`tw-rounded-lg tw-p-2 sm:tw-p-3 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-1 sm:tw-gap-3 tw-min-w-0 ${toneMap[tone] || toneMap.slate}`}>
            {icon && <Icon icon={icon} className="tw-text-2xl tw-opacity-80 tw-flex-shrink-0" />}
            <div className="tw-flex tw-flex-col tw-min-w-0">
                <span className="tw-text-[10px] tw-uppercase sm:tw-tracking-wide tw-font-semibold tw-opacity-80 tw-truncate">{label}</span>
                <span className="tw-text-lg tw-font-bold">{value ?? 0}</span>
            </div>
        </div>
    );
};

const StatusChips = ({ byStatus }) => {
    const entries = Object.entries(byStatus || {});
    if (entries.length === 0) return <span className="tw-text-xs tw-text-gray-400">No data</span>;
    return (
        <div className="tw-flex tw-flex-wrap tw-gap-1.5">
            {entries.map(([status, count]) => (
                <span
                    key={status}
                    className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-semibold ${STATUS_STYLE[status] || 'tw-bg-gray-100 tw-text-gray-600'}`}
                >
                    {humanize(status)}
                    <span className="tw-bg-white/60 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-font-bold">{count}</span>
                </span>
            ))}
        </div>
    );
};

const TopAssignees = ({ rows }) => {
    if (!rows || rows.length === 0) {
        return <div className="tw-text-xs tw-text-gray-400 tw-py-4 tw-text-center">No open assignments</div>;
    }
    // Percent bars are relative to the biggest bucket so movement is visible
    // even when volumes are small.
    const max = Math.max(...rows.map(r => r.count));
    return (
        <ul className="tw-space-y-2">
            {rows.map((r) => {
                const pct = max > 0 ? Math.round((r.count / max) * 100) : 0;
                return (
                    <li key={r.user_id} className="tw-flex tw-items-center tw-gap-2 tw-text-xs">
                        <div className="tw-flex-1 tw-min-w-0">
                            <div className="tw-flex tw-justify-between tw-mb-1">
                                <span className="tw-truncate tw-font-medium tw-text-gray-700">{r.name}</span>
                                <span className="tw-font-bold tw-text-gray-800">{r.count}</span>
                            </div>
                            <div className="tw-h-1.5 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden">
                                <div className="tw-h-full tw-bg-rose-400" style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

const TrendChart = ({ series, colorRaised = '#f43f5e', colorResolved = '#10b981' }) => {
    const chart = useMemo(() => {
        const days = series.map(p => p.day);
        return {
            options: {
                chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false }, animations: { enabled: false } },
                stroke: { curve: 'smooth', width: 2 },
                dataLabels: { enabled: false },
                fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0 } },
                xaxis: {
                    categories: days,
                    labels: { style: { fontSize: '10px' }, rotate: 0, hideOverlappingLabels: true },
                    tickAmount: 6,
                    axisBorder: { show: false },
                    axisTicks: { show: false }
                },
                yaxis: { labels: { style: { fontSize: '10px' } } },
                grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
                colors: [colorRaised, colorResolved],
                legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px' },
                tooltip: { x: { format: 'yyyy-MM-dd' } }
            },
            series: [
                { name: 'Raised',   data: series.map(p => p.raised) },
                { name: 'Resolved', data: series.map(p => p.resolved) }
            ]
        };
    }, [series, colorRaised, colorResolved]);

    return <ReactApexChart options={chart.options} series={chart.series} type="area" height={180} />;
};

const ModuleCard = ({ title, icon, tone, metrics, colorRaised, colorResolved, viewAllHref, onViewAll }) => {
    const trend = metrics?.trend || [];
    return (
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden tw-flex tw-flex-col">
            <div className="tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-border-b tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Icon icon={icon} className={`tw-text-xl tw-text-${tone}-600`} />
                    <h4 className="tw-text-sm tw-font-bold tw-text-gray-800">{title}</h4>
                </div>
                {(viewAllHref || onViewAll) && (
                    <button
                        type="button"
                        onClick={onViewAll}
                        className="tw-text-[11px] tw-font-medium tw-text-gray-500 hover:tw-text-gray-800 tw-inline-flex tw-items-center tw-gap-0.5"
                    >
                        View all <Icon icon="mdi:chevron-right" className="tw-text-base" />
                    </button>
                )}
            </div>

            <div className="tw-p-3 sm:tw-p-4 tw-flex tw-flex-col tw-gap-4">
                {/* KPI row — total / overdue / (escalated for complaints) */}
                <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                    <KpiCard label="Total"    value={metrics?.total}    tone="blue"    icon="mdi:counter" />
                    <KpiCard label="Overdue"  value={metrics?.overdue}  tone="rose"    icon="mdi:timer-alert-outline" />
                    <KpiCard
                        label={metrics?.escalated != null ? 'Escalated' : 'Open'}
                        value={metrics?.escalated != null ? metrics.escalated : Object.entries(metrics?.byStatus || {}).filter(([s]) => s !== 'COMPLETED' && s !== 'CLOSED' && s !== 'CANCELLED').reduce((n, [, c]) => n + Number(c || 0), 0)}
                        tone="amber"
                        icon="mdi:bell-alert-outline"
                    />
                </div>

                {/* Status distribution as chips */}
                <div>
                    <div className="tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">By Status</div>
                    <StatusChips byStatus={metrics?.byStatus} />
                </div>

                {/* Top assignees */}
                <div>
                    <div className="tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-2">Top Assignees</div>
                    <TopAssignees rows={metrics?.topAssignees || []} />
                </div>

                {/* 30-day trend */}
                <div>
                    <div className="tw-text-[11px] tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1">
                        Last 30 Days — Raised vs Resolved
                    </div>
                    {trend.length > 0 ? (
                        <TrendChart series={trend} colorRaised={colorRaised} colorResolved={colorResolved} />
                    ) : (
                        <div className="tw-h-[180px] tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-gray-400">No data</div>
                    )}
                </div>
            </div>
        </div>
    );
};

const OperationsSection = ({ data, loading, error, onRetry, filterSlot, onOpenComplaints, onOpenTasks }) => {
    const complaints = data?.complaints;
    const tasks = data?.tasks;

    return (
        <div className="col-12">
            <div className="tw-bg-slate-50 tw-rounded-xl tw-p-2.5 sm:tw-p-4 tw-border tw-border-slate-100">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <Icon icon="mdi:view-dashboard-outline" className="tw-text-xl tw-text-slate-700" />
                        <h3 className="tw-text-base tw-font-bold tw-text-gray-800">Operations Overview</h3>
                        {loading && (
                            <div className="tw-animate-spin tw-rounded-full tw-h-4 tw-w-4 tw-border-2 tw-border-gray-200 tw-border-t-rose-500 tw-ml-1"></div>
                        )}
                    </div>
                    {filterSlot}
                </div>

                {/* A failed load would otherwise render as a page of zeros */}
                {error && !loading && (
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-3 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-rose-50 tw-border tw-border-rose-100 tw-text-xs tw-text-rose-700">
                        <span className="tw-flex tw-items-center tw-gap-1.5">
                            <Icon icon="mdi:alert-circle-outline" className="tw-text-base" />
                            Couldn't load complaints &amp; tasks: {error}
                        </span>
                        {onRetry && (
                            <button type="button" onClick={onRetry} className="tw-font-medium tw-underline hover:tw-text-rose-800">
                                Retry
                            </button>
                        )}
                    </div>
                )}

                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
                    <ModuleCard
                        title="Complaints"
                        icon="mdi:message-alert-outline"
                        tone="rose"
                        metrics={complaints}
                        colorRaised="#f43f5e"
                        colorResolved="#10b981"
                        onViewAll={onOpenComplaints}
                    />
                    <ModuleCard
                        title="Tasks"
                        icon="mdi:clipboard-list-outline"
                        tone="indigo"
                        metrics={tasks}
                        colorRaised="#6366f1"
                        colorResolved="#10b981"
                        onViewAll={onOpenTasks}
                    />
                </div>
            </div>
        </div>
    );
};

export default OperationsSection;
